<template>
  <section class="gx-mc custom-el-wrapper">
    <gx-loading :show="loading && !loaded" text="加载搬家配置..." />

    <header class="gx-mc__head">
      <button type="button" class="gx-mc__back" @click="requestBack">
        <span class="gx-mc__back-icon">‹</span>
        返回微店列表
      </button>
      <div class="gx-mc__heading">
        <h1 class="gx-mc__title">{{ isGlobalShop ? '系统预设属性' : '搬家配置' }}</h1>
        <span v-if="activeShopName" class="gx-mc__shop">{{ activeShopName }}</span>
        <span class="gx-mc__plat">微店</span>
      </div>
      <div class="gx-mc__head-actions">
        <span class="gx-mc__shop-label">当前店铺</span>
        <el-select
          v-model="selectedShopId"
          class="gx-mc__shop-select"
          filterable
          :loading="shopsLoading"
          :disabled="shopsLoading || !shops.length || templateManageSaving || tierSaving || clearingAttributes"
          placeholder="请选择微店"
          no-data-text="暂无微店"
          @change="onShopSelect">
          <el-option v-for="item in shops" :key="item.shop_id" :label="item.shop_name" :value="Number(item.shop_id)">
            <span class="gx-mc__option-row">
              <span class="gx-mc__option-name">{{ item.shop_name }}</span>
              <span class="gx-mc__option-tags">
                <span class="gx-mc__option-meta">{{ item.group_name || '未分组' }}</span>
                <span v-if="Number(item.status) !== 1" class="gx-mc__option-warn">商品应用异常</span>
              </span>
            </span>
          </el-option>
        </el-select>
        <button
          type="button"
          class="gx-mc__refresh"
          :disabled="loading || templateManageSaving || tierSaving || imageSaving || clearingAttributes"
          @click="refreshConfig">
          刷新
        </button>
      </div>
    </header>

    <div v-if="shopsError" class="gx-mc__inline-error">
      {{ shopsError }}
      <button type="button" @click="loadShopOptions">重试</button>
    </div>

    <nav class="gx-mc__tabs" role="tablist" aria-label="搬家配置分类">
      <button
        v-for="tab in topTabs"
        :key="tab.key"
        type="button"
        class="gx-mc__tab"
        :class="{ 'gx-mc__tab--on': activeTab === tab.key }"
        role="tab"
        :aria-selected="activeTab === tab.key"
        @click="switchTab(tab.key)">
        {{ tab.label }}
        <span v-if="tab.key === 'basic' && (tierDirty || imageDirty)" class="gx-mc__tab-dot" aria-label="有未保存修改"></span>
        <span
          v-if="tab.key === 'attribute' && (templateManageHasChanges || attributeDirty)"
          class="gx-mc__tab-dot"
          aria-label="有未保存修改"></span>
      </button>
    </nav>

    <div v-if="loadError" class="gx-mc__state-card gx-mc__state-card--error">
      <i class="el-icon-warning-outline"></i>
      <div class="gx-mc__state-body">
        <div class="gx-mc__state-title">搬家配置加载失败</div>
        <div class="gx-mc__state-text">{{ loadError }}</div>
      </div>
      <div class="gx-mc__state-actions">
        <el-button size="small" @click="requestBack">返回微店列表</el-button>
        <el-button size="small" type="primary" :loading="loading" @click="refreshConfig">重新加载</el-button>
      </div>
    </div>

    <div v-else-if="shopsError && !shopId" class="gx-mc__state-card gx-mc__state-card--error">
      <i class="el-icon-warning-outline"></i>
      <div class="gx-mc__state-body">
        <div class="gx-mc__state-title">店铺列表加载失败</div>
        <div class="gx-mc__state-text">{{ shopsError }}</div>
      </div>
      <div class="gx-mc__state-actions">
        <el-button size="small" @click="requestBack">返回微店列表</el-button>
        <el-button size="small" type="primary" :loading="shopsLoading" @click="loadShopOptions">重新加载</el-button>
      </div>
    </div>

    <div v-else-if="!shopId && !shopsLoading && !shops.length && !shopsError" class="gx-mc__state-card">
      <i class="el-icon-folder-opened"></i>
      <div class="gx-mc__state-body">
        <div class="gx-mc__state-title">暂无可用微店</div>
        <div class="gx-mc__state-text">当前账号下没有可配置的微店，请先在微店列表完成授权。</div>
      </div>
      <div class="gx-mc__state-actions">
        <el-button size="small" @click="requestBack">返回微店列表</el-button>
      </div>
    </div>

    <div v-else-if="!loaded" class="gx-mc__state-card">
      <i class="el-icon-loading"></i>
      <div class="gx-mc__state-body">
        <div class="gx-mc__state-title">{{ shopsLoading ? '正在加载店铺列表' : '正在加载搬家配置' }}</div>
        <div class="gx-mc__state-text">请稍候...</div>
      </div>
    </div>

    <template v-else>
      <div v-show="activeTab === 'basic'" class="gx-mc__tab-panel gx-mc__basic-layout">
        <aside class="gx-mc__basic-nav" aria-label="基本配置导航">
          <button
            v-for="section in basicSections"
            :key="section.key"
            type="button"
            class="gx-mc__basic-nav-item"
            :class="{ 'gx-mc__basic-nav-item--on': activeBasicSection === section.key }"
            @click="switchBasicSection(section.key)">
            <span>{{ section.label }}</span>
            <span
              v-if="section.key === 'pricing' && tierDirty"
              class="gx-mc__tab-dot"
              aria-label="有未保存的阶梯价"></span>
            <span
              v-if="section.key === 'image' && imageDirty"
              class="gx-mc__tab-dot"
              aria-label="有未保存的图片设置"></span>
          </button>
        </aside>
        <div class="gx-mc__basic-content">
          <section v-show="activeBasicSection === 'pricing'" class="gx-mc__install-card">
            <div class="gx-mc__install-body">
              <template>
                <el-alert v-if="tierError" class="gx-mc__alert" type="error" :closable="false" :title="tierError" />
                <div class="gx-setting__row">
                  <span class="gx-setting__label">阶梯价售卖：</span>
                  <el-switch
                    v-model="tierEnabled"
                    class="gx-switch"
                    active-text="开启"
                    :disabled="tierLoading || !tierLoaded || Boolean(tierError) || tierSaving"
                    @change="onTierEnabledChange" />
                  <span class="gx-setting__hint">
                    <i class="el-icon-info"></i>
                    当前已使用阶梯售卖梯价，优先售卖价设置，SKU价将以阶梯售卖价中的价格规则重新计算
                  </span>
                </div>
                <template v-if="tierEnabled">
                  <div v-for="(rule, index) in tierRules" :key="index" class="gx-rule">
                    <div class="gx-rule__line">
                      <el-input v-model.trim="rule.min_price" class="gx-rule__price" placeholder="10" @input="rule.min_price = limitDecimal2(rule.min_price)" />
                      <span class="gx-rule__range">&lt;= 来源商品SKU价 &lt;</span>
                      <el-input v-model.trim="rule.max_price" class="gx-rule__price" placeholder="无上限" @input="rule.max_price = limitDecimal2(rule.max_price)" />
                      <span class="gx-rule__ops">
                        <button
                          type="button"
                          class="gx-rule__delete"
                          title="删除该档"
                          :disabled="tierSaving"
                          @click="removeTierRule(index)">
                          <i class="el-icon-delete"></i>
                        </button>
                        <button
                          v-if="index === tierRules.length - 1"
                          type="button"
                          class="gx-rule__add"
                          title="添加一档"
                          :disabled="tierSaving"
                          @click="addTierRule">
                          +
                        </button>
                      </span>
                    </div>
                    <div class="gx-rule__line">
                      <span class="gx-rule__label">来源SKU价：<em>*</em></span>
                      <el-input v-model.trim="rule.rate" class="gx-rule__rate" placeholder="0.00" @input="rule.rate = limitDecimal2(rule.rate)" />
                      <span class="gx-rule__unit">% +</span>
                      <el-input v-model.trim="rule.add_amount" class="gx-rule__amount" placeholder="请输入数字" @input="rule.add_amount = limitDecimal2(rule.add_amount)" />
                      <span class="gx-rule__unit">元</span>
                      <el-tooltip placement="top" content="按来源SKU价折算：来源SKU价 × 百分比 + 加价金额，得到本档计算价。">
                        <i class="el-icon-info gx-rule__tip"></i>
                      </el-tooltip>
                    </div>
                    <div class="gx-rule__line">
                      <span class="gx-rule__label">尾数处理：</span>
                      <span class="gx-rule__sub-label">尾数金额：</span>
                      <el-input
                        v-model.trim="rule.tail_amount"
                        class="gx-rule__tail"
                        maxlength="1"
                        placeholder="请输入"
                        @input="rule.tail_amount = limitDigits(rule.tail_amount, 1)" />
                      <el-tooltip placement="top" content="补足尾数价格：例如一个商品价格是60元，此值输入8，价格则会变为68。若计算后的价格小于10元，则按计算后的价格为准，如计算后的价格为9元，此值输入8，则价格按9元生效">
                        <i class="el-icon-info gx-rule__tip"></i>
                      </el-tooltip>
                      <span class="gx-rule__label">小数处理：</span>
                      <el-select
                        v-model="rule.decimal_mode"
                        class="gx-rule__mode"
                        placeholder="请选择"
                        @change="onTierDecimalModeChange(rule)">
                        <el-option
                          v-for="mode in tierDecimalModes"
                          :key="mode.value"
                          :label="mode.label"
                          :value="mode.value" />
                      </el-select>
                      <el-tooltip placement="top" content="例如商品计算后的价格为88.24元，小数处理手动设置99，则价格为88.99元。（注意：部分类目可能不生效。）">
                        <i class="el-icon-info gx-rule__tip"></i>
                      </el-tooltip>
                      <template v-if="rule.decimal_mode === 'manual'">
                        <span class="gx-rule__sub-label">手动小数值：</span>
                        <el-input
                          v-model.trim="rule.decimal_value"
                          class="gx-rule__decimal"
                          maxlength="2"
                          placeholder="请输入"
                          @input="rule.decimal_value = limitDigits(rule.decimal_value, 2)" />
                      </template>
                    </div>
                  </div>
                  <el-button
                    v-if="!tierRules.length"
                    class="gx-rule__empty-add"
                    :disabled="tierSaving || Boolean(tierError) || tierLoading"
                    @click="addTierRule"
                    >添加一档</el-button
                  >
                  <p v-if="tierLoading" class="gx-mc__install-tip">
                    <i class="el-icon-loading"></i>正在读取当前模板的阶梯价配置…
                  </p>
                  <p v-else class="gx-mc__install-tip">
                    <i class="el-icon-info"></i>区间按来源商品SKU价左闭右开匹配：命中档位按阶梯规则计算（来源SKU价 ×
                    百分比 + 加价 + 策略运费，不再叠加策略系数加价）；未命中或未启用则按上架策略计算（系数加价 +
                    运费）；计算价低于10元时不处理尾数。
                  </p>
                </template>
              </template>
              <div class="gx-mc__install-footer">
                <div class="gx-mc__install-footer-note">阶梯价按当前店铺和搬家配置模板分别保存。</div>
                <div class="gx-mc__install-footer-actions">
                  <span class="gx-mc__footer-label">搬家配置模板</span>
                  <el-select
                    v-model="templateSelectValue"
                    :key="'basic-' + templateSelectKey"
                    class="gx-mc__footer-template"
                    filterable
                    placeholder="请选择模板"
                    :disabled="!templates.length || templateManageSaving || tierSaving">
                    <el-option v-for="item in templates" :key="item.id" :label="item.name" :value="Number(item.id)"
                      ><span>{{ item.name }}</span
                      ><span v-if="Number(item.is_default) === 1" class="gx-mc__default-mark">默认</span></el-option
                    >
                  </el-select>
                  <el-button type="text" @click="openTemplateManage"
                    >管理模板<span
                      v-if="templateManageHasChanges"
                      class="gx-mc__tab-dot"
                      aria-label="有未保存模板名称"></span
                  ></el-button>
                  <el-button type="primary" :loading="tierSaving" :disabled="!tierCanSave" @click="saveTierConfig"
                    >保存</el-button
                  >
                </div>
              </div>
            </div>
          </section>
          <section v-show="activeBasicSection === 'image'" class="gx-mc__install-card">
            <div class="gx-mc__install-body">
              <template>
                <el-alert v-if="imageError" class="gx-mc__alert" type="error" :closable="false" :title="imageError" />
                <div class="gx-setting__row">
                  <span class="gx-setting__label">详情图没有时：</span>
                  <el-radio-group
                    v-model="detailMissingAction"
                    :disabled="imageLoading || imageSaving || Boolean(imageError)"
                    @change="imageDirty = true">
                    <el-radio label="random_main">随机用主图</el-radio>
                    <el-radio label="keep">保持原样</el-radio>
                  </el-radio-group>
                </div>
                <div class="gx-setting__row">
                  <span class="gx-setting__label">详情图有时：</span>
                  <el-radio-group
                    v-model="detailExistsAction"
                    :disabled="imageLoading || imageSaving || Boolean(imageError)"
                    @change="imageDirty = true">
                    <el-radio label="use_detail">使用详情图</el-radio>
                    <el-radio label="replace_with_main">主图替换详情图（去掉原详情图，用主图去上）</el-radio>
                  </el-radio-group>
                </div>
                <p v-if="imageLoading" class="gx-mc__install-tip">
                  <i class="el-icon-loading"></i>正在读取当前模板的图片设置…
                </p>
                <p v-else class="gx-mc__install-tip">
                  <i class="el-icon-info"></i>铺货时使用默认模板中的图片设置；详情图为空且选择“随机用主图”时，会将全部主图随机排序后作为详情图。
                </p>
              </template>
              <div class="gx-mc__install-footer">
                <div class="gx-mc__install-footer-note">图片设置按当前店铺和搬家配置模板分别保存。</div>
                <div class="gx-mc__install-footer-actions">
                  <span class="gx-mc__footer-label">搬家配置模板</span>
                  <el-select
                    v-model="templateSelectValue"
                    :key="'image-' + templateSelectKey"
                    class="gx-mc__footer-template"
                    filterable
                    placeholder="请选择模板"
                    :disabled="!templates.length || templateManageSaving || imageSaving">
                    <el-option v-for="item in templates" :key="item.id" :label="item.name" :value="Number(item.id)"
                      ><span>{{ item.name }}</span
                      ><span v-if="Number(item.is_default) === 1" class="gx-mc__default-mark">默认</span></el-option
                    >
                  </el-select>
                  <el-button type="text" @click="openTemplateManage"
                    >管理模板<span
                      v-if="templateManageHasChanges"
                      class="gx-mc__tab-dot"
                      aria-label="有未保存模板名称"></span
                  ></el-button>
                  <el-button type="primary" :loading="imageSaving" :disabled="!imageCanSave" @click="saveImageSettings"
                    >保存</el-button
                  >
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div v-show="activeTab === 'attribute'" class="gx-mc__tab-panel gx-mc__attribute-tab">
        <aside class="gx-mc__inner-nav" aria-label="属性设置导航">
          <button
            v-for="section in attributeSections"
            :key="section.key"
            type="button"
            class="gx-mc__inner-nav-item"
            :class="{ 'gx-mc__inner-nav-item--on': activeAttributeSection === section.key }"
            @click="switchAttributeSection(section.key)">
            <span>{{ section.label }}</span>
            <span
              v-if="section.key === 'template' && templateManageHasChanges"
              class="gx-mc__tab-dot"
              aria-label="有未保存模板名称"></span>
            <span
              v-if="section.key === 'attribute' && attributeDirty"
              class="gx-mc__tab-dot"
              aria-label="有未保存修改"></span>
          </button>
        </aside>

        <div class="gx-mc__inner-content">
          <div v-show="activeAttributeSection === 'basic'" class="gx-mc__inner-panel">
            <section class="gx-mc__dialog-card">
              <div class="gx-mc__dialog-head">
                <h2 class="gx-mc__dialog-title">{{ isGlobalShop ? '系统预设属性' : '类目属性设置' }}</h2>
                <div class="gx-mc__dialog-stats">
                  <span class="gx-mc__stat-badge"
                    >{{ isGlobalShop ? '系统预设已配置' : '当前模板已配置' }} {{ activeConfiguredCount }} 个类目</span
                  >
                  <span class="gx-mc__stat-badge"
                    >{{ isGlobalShop ? '系统预设可配置' : '可经营类目' }} {{ categoryCount }} 个类目</span
                  >
                </div>
              </div>

              <div class="gx-mc__dialog-actions">
                <el-button
                  type="primary"
                  class="gx-mc__dialog-primary"
                  :disabled="(!isGlobalShop && !activeTemplateId) || categoryCount === 0"
                  @click="switchAttributeSection('attribute')"
                  >{{ isGlobalShop ? '配置系统预设属性' : '类目属性设置' }}</el-button
                >
                <el-button
                  v-if="!isGlobalShop"
                  class="gx-mc__dialog-secondary"
                  :loading="refreshingCategories"
                  :disabled="refreshingCategories || tierSaving || templateManageSaving"
                  @click="refreshCategories"
                  >刷新微店类目</el-button
                >
                <el-button
                  v-if="!isGlobalShop"
                  class="gx-mc__dialog-secondary"
                  :loading="clearingAttributes"
                  :disabled="!activeTemplateId || activeConfiguredCount === 0 || templateManageSaving"
                  @click="clearTemplateAttributes"
                  >清空配置</el-button
                >
                <el-button
                  v-if="!isGlobalShop"
                  class="gx-mc__dialog-secondary"
                  :loading="globalImporting"
                  :disabled="!activeTemplateId || templateManageSaving"
                  @click="importGlobalAttributes"
                  >同步系统预设属性</el-button
                >
              </div>

              <p class="gx-mc__dialog-tip">
                <i class="el-icon-info"></i>
                {{
                  isGlobalShop
                    ? '这里配置的是系统预设属性，普通店铺需手动确认后覆盖到自己的模板。'
                    : '类目来自微店可经营类目；仅在类目与微店后台不一致时才需要刷新。'
                }}
              </p>

              <div v-if="!isGlobalShop" class="gx-mc__dialog-footer">
                <div class="gx-mc__dialog-note">
                  <i class="el-icon-info"></i>
                  铺货时将使用设置为【默认】模板中的配置
                </div>
                <div class="gx-mc__dialog-footer-actions">
                  <span class="gx-mc__footer-label">搬家配置模板</span>
                  <el-select
                    v-model="templateSelectValue"
                    :key="'inner-basic-' + templateSelectKey"
                    class="gx-mc__footer-template"
                    filterable
                    placeholder="请选择模板"
                    :disabled="!templates.length || templateManageSaving || tierSaving">
                    <el-option v-for="item in templates" :key="item.id" :label="item.name" :value="Number(item.id)">
                      <span>{{ item.name }}</span>
                      <span v-if="Number(item.is_default) === 1" class="gx-mc__default-mark">默认</span>
                    </el-option>
                  </el-select>
                  <el-button type="text" @click="openTemplateManage">
                    管理模板
                    <span v-if="templateManageHasChanges" class="gx-mc__tab-dot" aria-label="有未保存模板名称"></span>
                  </el-button>
                  <el-button
                    type="primary"
                    :loading="syncTargetsLoading"
                    :disabled="!activeTemplateId || syncSaving || templateManageSaving"
                    @click="openTemplateSyncDialog"
                    >同步保存到多店</el-button
                  >
                </div>
              </div>
            </section>
          </div>

          <div v-show="activeAttributeSection === 'attribute'" class="gx-mc__inner-panel">
            <div class="gx-mc__attribute-panel">
              <wechat-category-attribute-editor
                ref="attributeEditor"
                :shop-id="Number(shopId || 0)"
                :template-id="activeTemplateId"
                :global-mode="isGlobalShop"
                height="520px"
                @dirty-change="attributeDirty = $event"
                @saving-change="attributeSaving = $event"
                @category-change="attributeCanSave = $event"
                @saved="onAttributeSaved"
                @save-error="attributeDirty = true" />
            </div>
            <div class="gx-mc__section-footer">
              <div class="gx-mc__section-note">
                <span v-if="!isGlobalShop">铺货时使用默认模板中的配置</span>
                <span v-else>系统预设属性店铺</span>
              </div>
              <div class="gx-mc__section-actions">
                <template v-if="!isGlobalShop">
                  <span class="gx-mc__footer-label">搬家配置模板</span>
                  <el-select
                    v-model="templateSelectValue"
                    :key="'inner-attr-' + templateSelectKey"
                    class="gx-mc__footer-template"
                    filterable
                    placeholder="请选择模板"
                    :disabled="!templates.length || templateManageSaving || tierSaving">
                    <el-option v-for="item in templates" :key="item.id" :label="item.name" :value="Number(item.id)">
                      <span>{{ item.name }}</span>
                      <span v-if="Number(item.is_default) === 1" class="gx-mc__default-mark">默认</span>
                    </el-option>
                  </el-select>
                  <el-button type="text" @click="openTemplateManage">
                    管理模板
                    <span v-if="templateManageHasChanges" class="gx-mc__tab-dot" aria-label="有未保存模板名称"></span>
                  </el-button>
                  <el-button
                    type="primary"
                    :loading="syncTargetsLoading"
                    :disabled="!activeTemplateId || syncSaving || templateManageSaving"
                    @click="openTemplateSyncDialog"
                    >保存并同步至多店</el-button
                  >
                </template>
                <el-button
                  type="primary"
                  :loading="attributeSaving"
                  :disabled="!attributeCanSave || !attributeDirty || attributeSaving"
                  @click="saveCurrentCategory"
                  >保存</el-button
                >
              </div>
            </div>
          </div>

          <div v-if="!isGlobalShop" v-show="activeAttributeSection === 'template'" class="gx-mc__inner-panel">
            <div class="gx-mc__template-panel">
              <div class="gx-mc__panel-toolbar">
                <div class="gx-mc__panel-heading">
                  <h2 class="gx-mc__section-title">搬家配置模板</h2>
                  <p class="gx-mc__section-desc">铺货时使用默认模板中的配置；修改名称后点击“保存名称”。</p>
                </div>
                <div class="gx-mc__panel-actions">
                  <el-button
                    size="small"
                    :disabled="templateManageSaving || templateManageHasChanges"
                    @click="createTemplate(false)"
                    >新建模板</el-button
                  >
                  <el-button
                    size="small"
                    :disabled="!templateManageHasChanges || templateManageSaving"
                    @click="refreshTemplateManageRows"
                    >重置</el-button
                  >
                  <el-button
                    size="small"
                    type="primary"
                    :loading="templateManageSaving"
                    :disabled="!templateManageHasChanges"
                    @click="saveManagedTemplateNames"
                    >保存名称</el-button
                  >
                </div>
              </div>
              <el-table :data="templateManageRows" border max-height="520" empty-text="暂无模板">
                <el-table-column label="模板名称" min-width="360">
                  <template slot-scope="scope">
                    <div class="template-name-cell">
                      <el-input
                        v-model="scope.row.name"
                        maxlength="50"
                        show-word-limit
                        :disabled="templateManageSaving" />
                      <span v-if="Number(scope.row.is_default) === 1" class="default-template-mark">默认</span>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="已配置属性" width="130" align="center">
                  <template slot-scope="scope">{{ Number(scope.row.configured_count || 0) }}</template>
                </el-table-column>
                <el-table-column label="操作" width="280">
                  <template slot-scope="scope">
                    <el-button
                      type="text"
                      :disabled="templateManageSaving || templateManageHasChanges || Number(scope.row.is_default) === 1"
                      @click="setDefaultTemplate(scope.row)"
                      >设为默认</el-button
                    >
                    <el-button
                      type="text"
                      :disabled="templateManageSaving || templateManageHasChanges"
                      @click="createTemplate(true, scope.row)"
                      >复制模板</el-button
                    >
                    <el-button
                      type="text"
                      :disabled="
                        templateManageSaving ||
                        templateManageHasChanges ||
                        Number(scope.row.is_default) === 1 ||
                        templateManageRows.length <= 1
                      "
                      @click="deleteTemplate(scope.row)"
                      >删除</el-button
                    >
                  </template>
                </el-table-column>
              </el-table>
              <div class="gx-mc__section-footer gx-mc__template-footer">
                <div class="gx-mc__section-note">铺货时使用默认模板中的配置</div>
                <div class="gx-mc__section-actions">
                  <span class="gx-mc__footer-label">搬家配置模板</span>
                  <el-select
                    v-model="templateSelectValue"
                    :key="'inner-template-' + templateSelectKey"
                    class="gx-mc__footer-template"
                    filterable
                    placeholder="请选择模板"
                    :disabled="!templates.length || templateManageSaving || tierSaving">
                    <el-option v-for="item in templates" :key="item.id" :label="item.name" :value="Number(item.id)">
                      <span>{{ item.name }}</span>
                      <span v-if="Number(item.is_default) === 1" class="gx-mc__default-mark">默认</span>
                    </el-option>
                  </el-select>
                  <el-button
                    type="primary"
                    :loading="syncTargetsLoading"
                    :disabled="!activeTemplateId || syncSaving || templateManageSaving"
                    @click="openTemplateSyncDialog"
                    >保存并同步至多店</el-button
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <el-dialog
      v-if="!isGlobalShop"
      title="同步搬家配置到多店"
      :visible.sync="syncDialogVisible"
      width="640px"
      :close-on-click-modal="false"
      :show-close="!syncSaving"
      :close-on-press-escape="!syncSaving"
      append-to-body
      class="custom-el-wrapper">
      <div class="sync-dialog-tip">
        请选择目标店铺及搬家配置模板。同步后，目标模板的整套搬家配置和阶梯价规则将被当前模板覆盖。
      </div>
      <el-alert
        v-if="syncTargetsError"
        class="gx-mc__alert"
        type="error"
        :title="syncTargetsError"
        show-icon
        :closable="false">
        <el-button type="text" @click="openTemplateSyncDialog">重新加载目标店铺</el-button>
      </el-alert>
      <el-table
        v-loading="syncTargetsLoading"
        :data="syncTargets"
        border
        max-height="420"
        empty-text="暂无其他正常授权的微店">
        <el-table-column label="选择" width="70" align="center">
          <template slot-scope="scope">
            <el-checkbox v-model="scope.row.selected" :disabled="syncSaving" />
          </template>
        </el-table-column>
        <el-table-column prop="shop_name" label="店铺" min-width="190" />
        <el-table-column label="搬家配置模板" min-width="260">
          <template slot-scope="scope">
            <el-select
              v-model="scope.row.target_template_id"
              :disabled="!scope.row.selected || syncSaving"
              placeholder="请选择模板"
              class="sync-template-select">
              <el-option
                v-for="template in scope.row.templates"
                :key="template.id"
                :label="template.name"
                :value="Number(template.id)" />
            </el-select>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer">
        <el-button :disabled="syncSaving" @click="syncDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="syncSaving" @click="syncTemplateToShops">保存</el-button>
      </span>
    </el-dialog>
  </section>
</template>

<script>
import { mapActions } from 'vuex'

import GxLoading from '../../components/newUi/GxLoading.vue'
import WechatCategoryAttributeEditor from './components/WechatCategoryAttributeEditor.vue'

const SHOP_PAGE_SIZE = 1000

// 微店页面版搬家配置：布局与交互对齐 DoudianMoveConfigPage.vue，数据全部走微信搬家配置接口（shop_type=7）。
export default {
  name: 'WechatMoveConfigPage',
  components: { GxLoading, WechatCategoryAttributeEditor },
  props: {
    shopId: { type: Number, default: 0 }
  },
  data() {
    return {
      loading: false,
      loaded: false,
      loadError: '',
      requestSeq: 0,
      shops: [],
      shopsLoading: false,
      shopsError: '',
      shopListSeq: 0,
      selectedShopId: 0,
      shop: {},
      templates: [],
      activeTemplateId: '',
      categoryCount: 0,
      activeTab: 'basic',
      activeBasicSection: 'pricing',
      activeAttributeSection: 'basic',
      attributeDirty: false,
      attributeSaving: false,
      attributeCanSave: false,
      templateSelectKey: 0,
      templateManageSaving: false,
      templateManageRows: [],
      tierSeq: 0,
      tierLoading: false,
      tierLoaded: false,
      tierError: '',
      tierEnabled: false,
      tierRules: [],
      tierSaving: false,
      tierSnapshot: '',
      imageSeq: 0,
      imageLoading: false,
      imageLoaded: false,
      imageError: '',
      detailMissingAction: 'keep',
      detailExistsAction: 'use_detail',
      imageDirty: false,
      imageSaving: false,
      clearingAttributes: false,
      refreshingCategories: false,
      globalImporting: false,
      syncTargetsSeq: 0,
      syncTargetsLoading: false,
      syncTargetsError: '',
      syncSaving: false,
      syncDialogVisible: false,
      syncTargets: [],
      isGlobalShop: false,
      globalConfiguredCategoryCount: 0
    }
  },
  computed: {
    activeTemplate() {
      return this.templates.find(item => Number(item.id) === Number(this.activeTemplateId)) || {}
    },
    activeConfiguredCount() {
      if (this.isGlobalShop) return this.globalConfiguredCategoryCount
      return Number(this.activeTemplate.configured_count || 0)
    },
    selectedShop() {
      return this.shops.find(item => Number(item.shop_id) === Number(this.selectedShopId)) || {}
    },
    activeShopName() {
      return this.selectedShop.shop_name || this.shop.shop_name || ''
    },
    templateManageHasChanges() {
      return this.templateManageRows.some(item => String(item.name || '') !== String(item.original_name || ''))
    },
    topTabs() {
      return [
        { key: 'basic', label: '基本配置' },
        { key: 'attribute', label: '属性设置' }
      ]
    },
    basicSections() {
      return [
        { key: 'pricing', label: '价格设置' },
        { key: 'image', label: '图片设置' }
      ]
    },
    attributeSections() {
      const sections = [
        { key: 'basic', label: '基础配置' },
        { key: 'attribute', label: '属性设置' }
      ]
      if (!this.isGlobalShop) sections.push({ key: 'template', label: '模板管理' })
      return sections
    },
    tierDirty() {
      if (!this.tierLoaded || this.tierError) return false
      return this.tierSignature(this.tierPayload()) !== this.tierSnapshot
    },
    tierCanSave() {
      if (!this.activeTemplateId) return false
      if (this.tierLoading || this.tierSaving || this.tierError || !this.tierLoaded) return false
      if (!this.tierDirty) return false
      return !this.tierEnabled || this.tierRules.length > 0
    },
    imageCanSave() {
      if (!this.activeTemplateId) return false
      if (this.imageLoading || this.imageSaving || this.imageError || !this.imageLoaded) return false
      return this.imageDirty
    },
    tierDecimalModes() {
      return [
        { value: 'upstream', label: '与上游一致' },
        { value: 'integer', label: '不保留小数' },
        { value: '1_decimal', label: '保留1位小数' },
        { value: '2_decimal', label: '保留2位小数' },
        { value: 'manual', label: '手动设置' }
      ]
    },
    templateSelectValue: {
      get() {
        return this.activeTemplateId
      },
      set(value) {
        this.requestTemplateChange(value)
      }
    }
  },
  watch: {
    shopId(value) {
      const id = Number(value || 0)
      this.selectedShopId = id
      if (id > 0) {
        this.handleShopSwitch()
      } else {
        this.resetConfigState()
        this.ensureCurrentShop()
      }
    },
    activeTemplateId() {
      this.loadTierConfig()
      this.loadImageSettings()
    }
  },
  created() {
    this.selectedShopId = Number(this.shopId) || 0
    this.loadShopOptions()
    if (this.shopId > 0) this.loadConfig(false)
  },
  mounted() {
    this.scrollContentTop()
  },
  beforeDestroy() {
    this.requestSeq += 1
    this.shopListSeq += 1
    this.tierSeq += 1
    this.imageSeq += 1
    this.syncTargetsSeq += 1
  },
  methods: {
    ...mapActions({
      getShopList: 'ucenter/getDdShopList',
      getConfig: 'ucenter/getWechatMoveConfig',
      createTemplateAction: 'ucenter/createWechatMoveTemplate',
      renameTemplateAction: 'ucenter/renameWechatMoveTemplate',
      setDefaultTemplateAction: 'ucenter/setDefaultWechatMoveTemplate',
      deleteTemplateAction: 'ucenter/deleteWechatMoveTemplate',
      clearTemplateCategoryConfigs: 'ucenter/clearWechatMoveTemplateCategoryConfigs',
      refreshWechatCategories: 'ucenter/refreshWechatMoveCategories',
      getTierPricing: 'ucenter/getWechatMoveTemplatePricing',
      saveTierPricing: 'ucenter/saveWechatMoveTemplatePricing',
      getImageSettings: 'ucenter/getWechatMoveTemplateImageSettings',
      saveImageSettingsAction: 'ucenter/saveWechatMoveTemplateImageSettings',
      getTemplateSyncTargets: 'ucenter/getWechatMoveTemplateSyncTargets',
      syncTemplate: 'ucenter/syncWechatMoveTemplate',
      getGlobalConfigPreview: 'ucenter/getWechatMoveGlobalConfigPreview',
      importGlobalConfig: 'ucenter/importWechatMoveGlobalConfig'
    }),

    isCurrentShop(shopId) {
      return Number(this.shopId || 0) === Number(shopId || 0)
    },

    loadShopOptions() {
      const seq = ++this.shopListSeq
      this.shopsLoading = true
      this.shopsError = ''
      const hadShops = this.shops.length
      this.getShopList({
        data: { shop_type: 7, page: 1, page_size: SHOP_PAGE_SIZE },
        success: res => {
          if (seq !== this.shopListSeq) return
          this.shopsLoading = false
          if (res.code !== 200) {
            this.shopsError = res.msg || '店铺列表加载失败'
            if (!hadShops) this.ensureCurrentShop()
            return
          }
          const data = res.data || {}
          let rawList = []
          if (data.list && Array.isArray(data.list.list)) {
            rawList = data.list.list
          } else if (Array.isArray(data.list)) {
            rawList = data.list
          } else if (Array.isArray(data)) {
            rawList = data
          }
          this.shops = rawList.filter(item => item && item.shop_id)
          this.ensureCurrentShop()
        },
        fail: () => {
          if (seq !== this.shopListSeq) return
          this.shopsLoading = false
          this.shopsError = '店铺列表加载失败，请稍后重试'
          if (!hadShops) this.ensureCurrentShop()
        }
      })
    },

    ensureCurrentShop() {
      if (!this.shops.length) return
      const currentId = Number(this.shopId || this.selectedShopId || 0)
      const current = this.shops.find(item => Number(item.shop_id) === currentId)
      if (current) {
        this.selectedShopId = currentId
        return
      }
      if (currentId > 0 && this.loading) return
      if (currentId > 0 && this.shop.mer_shop_id && Number(this.shop.mer_shop_id) === currentId) {
        this.shops.unshift({
          shop_id: currentId,
          shop_name: this.shop.shop_name || `店铺 ${currentId}`,
          group_name: '当前店铺',
          status: this.shop.status
        })
        this.selectedShopId = currentId
        return
      }
      const firstShopId = Number(this.shops[0].shop_id)
      this.selectedShopId = firstShopId
      if (currentId > 0 && currentId !== firstShopId) {
        this.$message.warning('当前店铺不可用，已切换到第一家店铺')
      }
      if (String(this.$route.query.move_shop_id || '') === String(firstShopId)) {
        this.handleShopSwitch(firstShopId)
      } else {
        this.replaceShopQuery(firstShopId)
      }
    },

    replaceShopQuery(shopId) {
      if (String(this.$route.query.move_shop_id || '') === String(shopId)) return
      const fallbackShopId = Number(this.shopId) || 0
      this.$router
        .replace({
          path: this.$route.path,
          query: { ...this.$route.query, move_shop_id: String(shopId) }
        })
        .catch(() => {
          this.selectedShopId = fallbackShopId
        })
    },

    onShopSelect(shopId) {
      const id = Number(shopId || 0)
      const currentId = Number(this.shopId || 0)
      if (!id || id === currentId) {
        this.selectedShopId = currentId
        return
      }
      const proceed = () => this.replaceShopQuery(id)
      if (!this.hasUnsavedChanges()) {
        proceed()
        return
      }
      this.$confirm('当前存在未保存的配置，切换店铺后将丢失，是否继续？', '提示', { type: 'warning' })
        .then(proceed)
        .catch(() => {
          this.selectedShopId = currentId
        })
    },

    hasUnsavedChanges() {
      return Boolean(this.attributeDirty || this.tierDirty || this.imageDirty || this.templateManageHasChanges)
    },

    requestBack() {
      if (!this.hasUnsavedChanges()) {
        this.$emit('back')
        return
      }
      this.$confirm('当前存在未保存的配置，离开后将丢失，是否继续？', '提示', { type: 'warning' })
        .then(() => {
          this.discardChanges()
          this.$emit('back')
        })
        .catch(() => {})
    },

    discardChanges() {
      this.refreshTemplateManageRows()
      this.applyTierData({ enabled: this.tierEnabled, rules: this.tierPayload().rules })
      this.attributeDirty = false
      this.imageDirty = false
      if (this.$refs.attributeEditor) this.$refs.attributeEditor.discardDirty()
    },

    handleShopSwitch() {
      this.resetConfigState()
      this.loadConfig(false)
      this.scrollContentTop()
    },

    resetConfigState() {
      this.requestSeq += 1
      this.shop = {}
      this.templates = []
      this.activeTemplateId = ''
      this.categoryCount = 0
      this.isGlobalShop = false
      this.globalConfiguredCategoryCount = 0
      this.loading = false
      this.loaded = false
      this.loadError = ''
      this.clearingAttributes = false
      this.refreshingCategories = false
      this.globalImporting = false
      this.attributeDirty = false
      this.attributeSaving = false
      this.attributeCanSave = false
      this.templateManageSaving = false
      this.templateManageRows = []
      this.resetTierState()
      this.resetImageState()
      this.syncTargetsSeq += 1
      this.syncTargetsLoading = false
      this.syncTargetsError = ''
      this.syncSaving = false
      this.syncDialogVisible = false
      this.syncTargets = []
    },

    scrollContentTop() {
      this.$nextTick(() => {
        const main = document.getElementById('gx-main')
        if (main) main.scrollTop = 0
      })
    },

    switchBasicSection(key) {
      this.activeBasicSection = key
      this.scrollContentTop()
    },

    switchTab(key) {
      this.activeTab = key
      if (key === 'attribute' && this.activeAttributeSection === 'template' && !this.templateManageHasChanges) {
        this.refreshTemplateManageRows()
      }
      this.scrollContentTop()
    },

    switchAttributeSection(key) {
      this.activeAttributeSection = key
      if (key === 'template' && !this.templateManageHasChanges) this.refreshTemplateManageRows()
      this.scrollContentTop()
    },

    saveCurrentCategory() {
      if (this.attributeSaving) return
      if (this.$refs.attributeEditor) this.$refs.attributeEditor.save()
    },

    refreshAttributeEditor(keepCategory = true) {
      if (!this.$refs.attributeEditor) return
      this.$refs.attributeEditor.reload(keepCategory)
    },

    onAttributeSaved() {
      this.attributeDirty = false
      this.loadConfig()
    },

    requestTemplateChange(value) {
      const id = Number(value || 0)
      if (!id || id === Number(this.activeTemplateId)) return
      const proceed = () => {
        this.tierSeq += 1
        this.activeTemplateId = id
        this.refreshTemplateManageRows()
        this.discardChanges()
        this.loadConfig()
      }
      if (!this.hasUnsavedChanges()) {
        proceed()
        return
      }
      this.$confirm('当前存在未保存的配置，切换模板后将丢失，是否继续？', '提示', { type: 'warning' })
        .then(proceed)
        .catch(() => {
          this.templateSelectKey += 1
        })
    },

    loadConfig(keepActive = true) {
      const shopId = Number(this.shopId || 0)
      if (!shopId) {
        this.loading = false
        this.loaded = false
        this.loadError = ''
        return
      }

      const hadLoaded = this.loaded
      const seq = ++this.requestSeq
      const previousId = keepActive ? Number(this.activeTemplateId) : 0
      this.loading = true
      this.loadError = ''

      this.getConfig({
        shopId,
        success: res => {
          if (seq !== this.requestSeq) return
          this.loading = false
          if (res.code !== 200) {
            if (hadLoaded) {
              this.$message.error(res.msg || '获取搬家配置失败')
              return
            }
            this.loaded = false
            this.loadError = res.msg || '获取搬家配置失败'
            this.ensureCurrentShop()
            return
          }

          const data = res.data || {}
          if (!data.shop || !data.shop.mer_shop_id) {
            this.loaded = false
            this.loadError = '未获取到店铺信息，请返回微店列表重新进入'
            this.ensureCurrentShop()
            return
          }

          this.shop = data.shop
          this.templates = Array.isArray(data.templates) ? data.templates : []
          this.categoryCount = Number(data.category_count || 0)
          this.isGlobalShop = Boolean(data.is_global_attribute_shop)
          this.globalConfiguredCategoryCount = Number(data.configured_category_count || 0)
          const activeExists = this.templates.some(item => Number(item.id) === previousId)
          this.activeTemplateId = activeExists
            ? previousId
            : Number(data.default_template_id || (this.templates[0] || {}).id || 0)
          this.loaded = true
          this.loadError = ''
          if (!hadLoaded) this.scrollContentTop()
          if (this.isGlobalShop && this.activeAttributeSection === 'template') this.activeAttributeSection = 'basic'
          if (this.activeAttributeSection === 'template' && !this.templateManageHasChanges)
            this.refreshTemplateManageRows()
          this.ensureCurrentShop()
        },
        fail: () => {
          if (seq !== this.requestSeq) return
          this.loading = false
          if (hadLoaded) {
            this.$message.error('获取搬家配置失败，请稍后重试')
            return
          }
          this.loaded = false
          this.loadError = '获取搬家配置失败，请稍后重试'
          this.ensureCurrentShop()
        }
      })
    },

    refreshConfig() {
      if (this.loading) return
      if (this.hasUnsavedChanges()) {
        this.$confirm('当前有未保存的配置，刷新后将丢失，是否继续？', '提示', { type: 'warning' })
          .then(() => this.refreshConfigData())
          .catch(() => {})
        return
      }
      this.refreshConfigData()
    },

    refreshConfigData() {
      if (this.shopId > 0) {
        if (this.shopsError) this.loadShopOptions()
        this.loadConfig()
        this.loadTierConfig()
        this.loadImageSettings()
      } else {
        this.loadShopOptions()
      }
    },

    refreshTemplateManageRows() {
      this.templateManageRows = this.templates.map(item => ({
        ...item,
        original_name: item.name
      }))
    },

    openTemplateManage() {
      this.activeTab = 'attribute'
      this.activeAttributeSection = 'template'
      this.refreshTemplateManageRows()
      this.scrollContentTop()
    },

    saveManagedTemplateNames() {
      const changedRows = this.templateManageRows.filter(
        item => String(item.name || '').trim() !== String(item.original_name || '')
      )
      if (changedRows.length === 0) return
      const invalid = changedRows.find(item => {
        const name = String(item.name || '').trim()
        return name.length === 0 || name.length > 50
      })
      if (invalid) {
        this.$message.error('模板名称长度应为1至50个字符')
        return
      }
      const actionShopId = Number(this.shopId || 0)
      this.templateManageSaving = true
      const saveNext = index => {
        if (!this.isCurrentShop(actionShopId)) return
        if (index >= changedRows.length) {
          this.templateManageSaving = false
          this.$message.success('模板名称已更新')
          this.loadConfig()
          return
        }
        const item = changedRows[index]
        this.renameTemplateAction({
          templateId: item.id,
          data: { name: String(item.name).trim() },
          success: res => {
            if (!this.isCurrentShop(actionShopId)) return
            if (res.code !== 200) {
              this.templateManageSaving = false
              this.$message.error(res.msg || '重命名失败')
              return
            }
            const sourceRow = this.templateManageRows.find(row => Number(row.id) === Number(item.id))
            if (sourceRow) sourceRow.original_name = String(item.name).trim()
            saveNext(index + 1)
          },
          fail: () => {
            if (!this.isCurrentShop(actionShopId)) return
            this.templateManageSaving = false
            this.$message.error('模板名称更新失败，请稍后重试')
          }
        })
      }
      saveNext(0)
    },

    createTemplate(copyCurrent, sourceTemplate = this.activeTemplate) {
      if (this.templateManageSaving) return
      if (this.templateManageHasChanges) {
        this.$message.warning('请先保存或重置模板名称修改')
        return
      }
      const actionShopId = Number(this.shopId || 0)
      const title = copyCurrent ? '复制模板' : '新建模板'
      const defaultName = copyCurrent ? `${sourceTemplate.name || '配置'}-副本` : ''
      this.$prompt('请输入模板名称', title, {
        inputValue: defaultName,
        inputValidator: value => {
          const name = String(value || '').trim()
          return name.length > 0 && name.length <= 50 ? true : '模板名称长度应为1至50个字符'
        }
      })
        .then(({ value }) => {
          if (!this.isCurrentShop(actionShopId)) return
          this.createTemplateAction({
            shopId: actionShopId,
            data: {
              name: String(value).trim(),
              copy_from_template_id: copyCurrent ? Number(sourceTemplate.id || 0) : 0
            },
            success: res => {
              if (!this.isCurrentShop(actionShopId)) return
              if (res.code !== 200) {
                this.$message.error(res.msg || '模板创建失败')
                return
              }
              this.activeTemplateId = Number((res.data || {}).id || 0)
              this.$message.success(res.msg || '模板创建成功')
              this.loadConfig()
            },
            fail: () => {
              if (!this.isCurrentShop(actionShopId)) return
              this.$message.error('模板创建失败，请稍后重试')
            }
          })
        })
        .catch(() => {})
    },

    setDefaultTemplate(template = this.activeTemplate) {
      if (!template || !template.id) return
      const actionShopId = Number(this.shopId || 0)
      this.$confirm(`确认将“${template.name}”设为默认模板？`, '提示', { type: 'warning' })
        .then(() => {
          if (!this.isCurrentShop(actionShopId)) return
          this.setDefaultTemplateAction({
            templateId: template.id,
            success: res => {
              if (!this.isCurrentShop(actionShopId)) return
              if (res.code !== 200) return this.$message.error(res.msg || '设置失败')
              this.$message.success(res.msg || '默认模板已更新')
              this.loadConfig()
            },
            fail: () => {
              if (!this.isCurrentShop(actionShopId)) return
              this.$message.error('设置默认模板失败，请稍后重试')
            }
          })
        })
        .catch(() => {})
    },

    deleteTemplate(template = this.activeTemplate) {
      if (!template || !template.id) return
      const actionShopId = Number(this.shopId || 0)
      this.$confirm(`删除模板“${template.name}”后，其类目属性配置也会删除。是否继续？`, '提示', { type: 'warning' })
        .then(() => {
          if (!this.isCurrentShop(actionShopId)) return
          this.deleteTemplateAction({
            templateId: template.id,
            success: res => {
              if (!this.isCurrentShop(actionShopId)) return
              if (res.code !== 200) return this.$message.error(res.msg || '删除失败')
              const deletingActive = Number(template.id) === Number(this.activeTemplateId)
              if (deletingActive) this.activeTemplateId = ''
              this.$message.success(res.msg || '模板已删除')
              this.loadConfig(!deletingActive)
            },
            fail: () => {
              if (!this.isCurrentShop(actionShopId)) return
              this.$message.error('删除模板失败，请稍后重试')
            }
          })
        })
        .catch(() => {})
    },

    clearTemplateAttributes() {
      if (!this.activeTemplateId) return
      const actionShopId = Number(this.shopId || 0)
      this.$confirm(`确认清空模板“${this.activeTemplate.name}”的全部类目属性配置？清空后无法恢复。`, '清空配置', {
        type: 'warning',
        confirmButtonText: '确认清空',
        cancelButtonText: '取消'
      })
        .then(() => {
          if (!this.isCurrentShop(actionShopId)) return
          this.clearingAttributes = true
          this.clearTemplateCategoryConfigs({
            templateId: this.activeTemplateId,
            success: res => {
              if (!this.isCurrentShop(actionShopId)) return
              this.clearingAttributes = false
              if (res.code !== 200) return this.$message.error(res.msg || '清空配置失败')
              this.$message.success(res.msg || '配置已清空')
              this.attributeDirty = false
              this.refreshAttributeEditor()
              this.loadConfig()
            },
            fail: () => {
              if (!this.isCurrentShop(actionShopId)) return
              this.clearingAttributes = false
              this.$message.error('清空配置失败，请稍后重试')
            }
          })
        })
        .catch(() => {})
    },

    refreshCategories() {
      const actionShopId = Number(this.shopId || 0)
      this.$confirm('刷新后将以微店后台当前可经营类目更新本地类目库，是否继续？', '刷新微店类目', {
        type: 'warning',
        confirmButtonText: '继续刷新',
        cancelButtonText: '取消'
      })
        .then(() => {
          if (!this.isCurrentShop(actionShopId)) return
          this.refreshingCategories = true
          this.refreshWechatCategories({
            shopId: actionShopId,
            success: res => {
              if (!this.isCurrentShop(actionShopId)) return
              this.refreshingCategories = false
              if (res.code !== 200) return this.$message.error(res.msg || '刷新类目失败')
              this.$message.success(res.msg || '可经营类目已刷新')
              this.refreshAttributeEditor(false)
              this.loadConfig()
            },
            fail: () => {
              if (!this.isCurrentShop(actionShopId)) return
              this.refreshingCategories = false
              this.$message.error('刷新类目失败，请稍后重试')
            }
          })
        })
        .catch(() => {})
    },

    importGlobalAttributes() {
      if (!this.activeTemplateId) return
      const actionShopId = Number(this.shopId || 0)
      this.globalImporting = true
      this.getGlobalConfigPreview({
        templateId: this.activeTemplateId,
        success: res => {
          if (!this.isCurrentShop(actionShopId)) return
          this.globalImporting = false
          if (res.code !== 200) return this.$message.error(res.msg || '获取系统预设属性预览失败')
          const data = res.data || {}
          if (!Number(data.importable_count || 0)) {
            return this.$message.warning('当前店铺没有可同步的系统预设属性')
          }
          const message = `同步系统预设属性会覆盖当前模板已配置的类目属性。系统已配置 ${
            data.global_config_count || 0
          } 个类目，当前店铺可同步 ${data.importable_count || 0} 个，并将清除 ${
            data.will_clear_count || 0
          } 个不在同步范围内的类目配置。是否继续？`
          this.$confirm(message, '二次确认', {
            type: 'warning',
            confirmButtonText: '确认同步',
            cancelButtonText: '取消'
          })
            .then(() => this.executeGlobalImport(actionShopId))
            .catch(() => {})
        },
        fail: () => {
          if (!this.isCurrentShop(actionShopId)) return
          this.globalImporting = false
          this.$message.error('获取系统预设属性预览失败，请稍后重试')
        }
      })
    },

    executeGlobalImport(actionShopId = Number(this.shopId || 0)) {
      if (!this.isCurrentShop(actionShopId)) return
      this.globalImporting = true
      this.importGlobalConfig({
        templateId: this.activeTemplateId,
        data: { confirmed: true },
        success: res => {
          if (!this.isCurrentShop(actionShopId)) return
          this.globalImporting = false
          if (res.code !== 200) return this.$message.error(res.msg || '同步系统预设属性失败')
          this.$message.success(res.msg || '系统预设属性已同步到当前模板')
          this.attributeDirty = false
          this.refreshAttributeEditor()
          this.loadConfig()
        },
        fail: () => {
          if (!this.isCurrentShop(actionShopId)) return
          this.globalImporting = false
          this.$message.error('同步系统预设属性失败，请稍后重试')
        }
      })
    },

    resetTierState() {
      this.tierSeq += 1
      this.tierLoading = false
      this.tierLoaded = false
      this.tierError = ''
      this.tierEnabled = false
      this.tierRules = []
      this.tierSaving = false
      this.tierSnapshot = ''
    },

    tierPayload() {
      return {
        enabled: Boolean(this.tierEnabled),
        rules: this.tierRules.map(rule => ({
          min_price: String(rule.min_price || '').trim(),
          max_price: String(rule.max_price || '').trim(),
          rate: String(rule.rate || '').trim(),
          add_amount: String(rule.add_amount || '').trim(),
          tail_amount: String(rule.tail_amount || '').trim(),
          decimal_mode: String(rule.decimal_mode || '2_decimal'),
          decimal_value: rule.decimal_mode === 'manual' ? String(rule.decimal_value || '').trim() : ''
        }))
      }
    },

    tierSignature(data) {
      return JSON.stringify({ enabled: Boolean(data.enabled), rules: data.rules })
    },

    applyTierData(data) {
      this.tierEnabled = Boolean(data.enabled)
      this.tierRules = (Array.isArray(data.rules) ? data.rules : []).map(rule => ({
        min_price: rule.min_price === null || rule.min_price === undefined ? '' : String(rule.min_price),
        max_price: rule.max_price === null || rule.max_price === undefined ? '' : String(rule.max_price),
        rate: rule.rate === null || rule.rate === undefined ? '' : String(rule.rate),
        add_amount: rule.add_amount === null || rule.add_amount === undefined ? '' : String(rule.add_amount),
        tail_amount: rule.tail_amount === null || rule.tail_amount === undefined ? '' : String(rule.tail_amount),
        decimal_mode: this.tierDecimalModes.some(mode => mode.value === String(rule.decimal_mode))
          ? String(rule.decimal_mode)
          : '2_decimal',
        decimal_value: rule.decimal_value === null || rule.decimal_value === undefined ? '' : String(rule.decimal_value)
      }))
      this.tierSnapshot = this.tierSignature(this.tierPayload())
      if (this.tierEnabled && this.tierRules.length === 0) this.addTierRule()
    },

    loadTierConfig() {
      this.resetTierState()
      const seq = ++this.tierSeq
      if (!this.shopId || !this.activeTemplateId) return
      const templateId = Number(this.activeTemplateId)
      const shopId = Number(this.shopId || 0)
      this.tierLoading = true
      this.getTierPricing({
        templateId,
        success: res => {
          if (seq !== this.tierSeq || templateId !== Number(this.activeTemplateId) || !this.isCurrentShop(shopId))
            return
          this.tierLoading = false
          if (res.code !== 200) {
            this.tierError = res.msg || '读取阶梯价配置失败'
            this.tierLoaded = true
            return
          }
          const data = res.data && typeof res.data === 'object' ? res.data : {}
          if (!Object.prototype.hasOwnProperty.call(data, 'enabled')) {
            this.tierError = '阶梯价配置数据缺失，请刷新后重试'
            this.tierLoaded = true
            return
          }
          this.applyTierData(data)
          this.tierLoaded = true
        },
        fail: () => {
          if (seq !== this.tierSeq || templateId !== Number(this.activeTemplateId) || !this.isCurrentShop(shopId))
            return
          this.tierLoading = false
          this.tierLoaded = true
          this.tierError = '读取阶梯价配置失败，请稍后重试'
        }
      })
    },

    addTierRule() {
      if (this.tierSaving) return
      const last = this.tierRules[this.tierRules.length - 1] || {}
      this.tierRules.push({
        min_price: String(last.max_price || ''),
        max_price: '',
        rate: last.rate || '100',
        add_amount: '0',
        tail_amount: '',
        decimal_mode: '2_decimal',
        decimal_value: ''
      })
    },

    removeTierRule(index) {
      if (this.tierSaving) return
      this.tierRules.splice(index, 1)
    },

    limitDecimal2(value) {
      let text = String(value === null || value === undefined ? '' : value).replace(/[^\d.]/g, '')
      const dot = text.indexOf('.')
      if (dot !== -1) {
        text = text.slice(0, dot + 1) + text.slice(dot + 1).replace(/\./g, '').slice(0, 2)
      }
      return text.startsWith('.') ? '0' + text : text
    },

    limitDigits(value, max) {
      return String(value === null || value === undefined ? '' : value).replace(/\D/g, '').slice(0, max)
    },

    onTierDecimalModeChange(rule) {
      if (rule.decimal_mode === 'manual' && !String(rule.decimal_value || '').trim()) {
        rule.decimal_value = '00'
      }
    },

    onTierEnabledChange(enabled) {
      // 开启后没有任何档位时直接补一档，省去再点“添加一档”。
      if (enabled && this.tierRules.length === 0) this.addTierRule()
    },

    saveTierConfig() {
      if (!this.tierCanSave) return
      const templateId = Number(this.activeTemplateId)
      const shopId = Number(this.shopId || 0)
      const seq = this.tierSeq
      const payload = this.tierPayload()
      this.tierSaving = true
      this.saveTierPricing({
        templateId,
        data: payload,
        success: res => {
          if (seq !== this.tierSeq || templateId !== Number(this.activeTemplateId) || !this.isCurrentShop(shopId))
            return
          this.tierSaving = false
          if (res.code !== 200) return this.$message.error(res.msg || '保存阶梯价配置失败')
          this.applyTierData(res.data && typeof res.data === 'object' ? res.data : payload)
          this.$message.success(res.msg || '阶梯价配置已保存')
        },
        fail: () => {
          if (seq !== this.tierSeq || templateId !== Number(this.activeTemplateId) || !this.isCurrentShop(shopId))
            return
          this.tierSaving = false
          this.$message.error('保存阶梯价配置失败，请稍后重试')
        }
      })
    },

    resetImageState() {
      this.imageSeq += 1
      this.imageLoading = false
      this.imageLoaded = false
      this.imageError = ''
      this.detailMissingAction = 'keep'
      this.detailExistsAction = 'use_detail'
      this.imageDirty = false
      this.imageSaving = false
    },

    loadImageSettings() {
      this.resetImageState()
      const seq = ++this.imageSeq
      if (!this.shopId || !this.activeTemplateId) return
      const templateId = Number(this.activeTemplateId)
      const shopId = Number(this.shopId || 0)
      this.imageLoading = true
      this.getImageSettings({
        templateId,
        success: res => {
          if (seq !== this.imageSeq || templateId !== Number(this.activeTemplateId) || !this.isCurrentShop(shopId))
            return
          this.imageLoading = false
          if (res.code !== 200) {
            this.imageError = res.msg || '读取图片设置失败'
            return
          }
          const data = res.data && typeof res.data === 'object' ? res.data : {}
          const missing = String(data.detail_missing_action || '')
          const exists = String(data.detail_exists_action || '')
          if (!['random_main', 'keep'].includes(missing) || !['use_detail', 'replace_with_main'].includes(exists)) {
            this.imageError = '图片设置数据缺失或无效，请刷新后重试'
            return
          }
          this.detailMissingAction = missing
          this.detailExistsAction = exists
          this.imageLoaded = true
          this.imageDirty = false
        },
        fail: () => {
          if (seq !== this.imageSeq || templateId !== Number(this.activeTemplateId) || !this.isCurrentShop(shopId))
            return
          this.imageLoading = false
          this.imageError = '读取图片设置失败，请稍后重试'
        }
      })
    },

    saveImageSettings() {
      if (!this.imageCanSave) return
      const templateId = Number(this.activeTemplateId)
      const shopId = Number(this.shopId || 0)
      const seq = this.imageSeq
      const submitted = {
        detail_missing_action: this.detailMissingAction,
        detail_exists_action: this.detailExistsAction
      }
      this.imageSaving = true
      this.saveImageSettingsAction({
        templateId,
        data: submitted,
        success: res => {
          if (seq !== this.imageSeq || templateId !== Number(this.activeTemplateId) || !this.isCurrentShop(shopId))
            return
          this.imageSaving = false
          if (res.code !== 200) return this.$message.error(res.msg || '保存图片设置失败')
          const data = res.data && typeof res.data === 'object' ? res.data : submitted
          this.detailMissingAction = String(data.detail_missing_action || submitted.detail_missing_action)
          this.detailExistsAction = String(data.detail_exists_action || submitted.detail_exists_action)
          this.imageDirty = false
          this.$message.success(res.msg || '图片设置已保存')
        },
        fail: () => {
          if (seq !== this.imageSeq || templateId !== Number(this.activeTemplateId) || !this.isCurrentShop(shopId))
            return
          this.imageSaving = false
          this.$message.error('保存图片设置失败')
        }
      })
    },

    openTemplateSyncDialog() {
      if (!this.activeTemplateId) return
      const seq = ++this.syncTargetsSeq
      this.syncDialogVisible = true
      this.syncTargetsLoading = true
      this.syncTargets = []
      this.syncTargetsError = ''
      this.getTemplateSyncTargets({
        templateId: this.activeTemplateId,
        success: res => {
          if (seq !== this.syncTargetsSeq) return
          this.syncTargetsLoading = false
          if (res.code !== 200) {
            this.syncTargetsError = res.msg || '获取目标店铺失败'
            this.$message.error(this.syncTargetsError)
            return
          }
          const targets = Array.isArray(res.data) ? res.data : []
          this.syncTargets = targets.map(shop => {
            const templates = Array.isArray(shop.templates) ? shop.templates : []
            const defaultTemplate = templates.find(template => Number(template.is_default) === 1)
            return {
              ...shop,
              selected: false,
              target_template_id: Number((defaultTemplate || templates[0] || {}).id || 0)
            }
          })
        },
        fail: () => {
          if (seq !== this.syncTargetsSeq) return
          this.syncTargetsLoading = false
          this.syncTargetsError = '获取目标店铺失败，请稍后重试'
          this.$message.error(this.syncTargetsError)
        }
      })
    },

    syncTemplateToShops() {
      const actionShopId = Number(this.shopId || 0)
      const selected = this.syncTargets.filter(shop => shop.selected)
      if (selected.length === 0) return this.$message.error('请选择需要同步的店铺')
      if (selected.some(shop => !shop.target_template_id)) {
        return this.$message.error('请选择目标店铺的搬家配置模板')
      }
      this.$confirm(
        `确认将“${this.activeTemplate.name || '当前模板'}”的整套搬家配置覆盖到选中的 ${selected.length} 个店铺模板？`,
        '同步搬家配置',
        { type: 'warning', confirmButtonText: '确认同步', cancelButtonText: '取消' }
      )
        .then(() => {
          if (!this.isCurrentShop(actionShopId)) return
          this.syncSaving = true
          this.syncTemplate({
            templateId: this.activeTemplateId,
            data: { target_template_ids: selected.map(shop => shop.target_template_id) },
            success: res => {
              if (!this.isCurrentShop(actionShopId)) return
              this.syncSaving = false
              if (res.code !== 200) return this.$message.error(res.msg || '同步失败')
              this.syncDialogVisible = false
              this.$message.success(res.msg || '搬家配置同步成功')
            },
            fail: () => {
              if (!this.isCurrentShop(actionShopId)) return
              this.syncSaving = false
              this.$message.error('同步失败，请稍后重试')
            }
          })
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.gx-mc__basic-layout {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-top: 14px;
}

.gx-mc__basic-nav {
  position: sticky;
  top: 45px;
  flex: 0 0 180px;
  width: 180px;
  align-self: flex-start;
  padding: 10px;
  border: 1px solid #e0e0ec;
  border-radius: 8px;
  background: #ffffff;
}

.gx-mc__basic-nav-item {
  display: flex;
  align-items: center;
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: #4a4a6a;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  text-align: left;
}

.gx-mc__basic-nav-item--on {
  background: #fff5f5;
  color: #c8443c;
  font-weight: 600;
  box-shadow: inset 3px 0 0 #c8443c;
}

.gx-mc__basic-content {
  flex: 1;
  min-width: 0;
}

.gx-mc {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 96px);
  color: #1a1a2e;
  font-family: 'Noto Sans SC', sans-serif;
}
.gx-mc__head {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 2px 0 14px;
  border-bottom: 1px solid #e0e0ec;
}
.gx-mc__back {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #ffccc7;
  border-radius: 5px;
  background: #fff5f5;
  color: #c8443c;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
}
.gx-mc__back:hover {
  border-color: #c8443c;
  background: #ffecec;
}
.gx-mc__back-icon {
  font-size: 16px;
  line-height: 1;
}
.gx-mc__heading {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
}
.gx-mc__title {
  margin: 0;
  color: #1a1a2e;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
}
.gx-mc__shop {
  max-width: 300px;
  overflow: hidden;
  padding: 3px 10px;
  border-radius: 12px;
  background: #f4f6f8;
  color: #4a4a6a;
  font-size: 12px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.gx-mc__plat {
  padding: 3px 9px;
  border: 1px solid rgba(212, 169, 70, 0.34);
  border-radius: 12px;
  background: #fffbf0;
  color: #a67c1a;
  font-size: 12px;
  line-height: 18px;
}
.gx-mc__head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
}
.gx-mc__shop-label {
  color: #4a4a6a;
  font-size: 12px;
  white-space: nowrap;
}
.gx-mc__shop-select {
  width: 260px;
}
.gx-mc__refresh {
  height: 32px;
  padding: 0 14px;
  border: 1px solid #e0e0ec;
  border-radius: 5px;
  background: #ffffff;
  color: #4a4a6a;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  transition: all 0.2s;
}
.gx-mc__refresh:hover:not(:disabled) {
  border-color: rgba(212, 169, 70, 0.45);
  color: #a67c1a;
  background: #fffbf0;
}
.gx-mc__refresh:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.gx-mc__inline-error {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
  padding: 8px 12px;
  border: 1px solid #fbc4c0;
  border-radius: 6px;
  background: #fff5f5;
  color: #c8443c;
  font-size: 12px;
}
.gx-mc__inline-error button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #c8443c;
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
}
.gx-mc__tabs {
  position: sticky;
  top: 0;
  z-index: 12;
  display: flex;
  align-items: stretch;
  height: 45px;
  margin-top: 12px;
  border-bottom: 1px solid #e0e0ec;
  background: #ffffff;
  overflow-x: auto;
  scrollbar-width: none;
}
.gx-mc__tabs::-webkit-scrollbar {
  display: none;
}
.gx-mc__tab {
  position: relative;
  flex: none;
  padding: 0 20px;
  border: 0;
  background: transparent;
  color: #4a4a6a;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  white-space: nowrap;
}
.gx-mc__tab:hover {
  color: #c8443c;
}
.gx-mc__tab--on {
  color: #c8443c;
  font-weight: 600;
}
.gx-mc__tab--on::after {
  position: absolute;
  right: 16px;
  bottom: -1px;
  left: 16px;
  height: 2px;
  border-radius: 2px;
  background: #c8443c;
  content: '';
}
.gx-mc__tab-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-left: 5px;
  border-radius: 50%;
  background: #c8443c;
  vertical-align: middle;
}
.gx-mc__tab-panel {
  min-width: 0;
}
.gx-mc__section-title {
  margin: 0 0 12px;
  color: #1a1a2e;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}
.gx-mc__section-desc {
  margin: 0 0 12px;
  color: #8a95a0;
  font-size: 12px;
  line-height: 20px;
}
.gx-mc__alert {
  margin: 12px 0;
}
.gx-mc__default-mark {
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: #f6ffed;
  color: #52a65c;
  font-size: 11px;
  line-height: 18px;
}
.gx-mc__attribute-panel {
  margin-top: 14px;
  border: 1px solid #e0e0ec;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}
.gx-mc__template-panel {
  margin-top: 14px;
  padding: 18px 20px;
  border: 1px solid #e0e0ec;
  border-radius: 8px;
  background: #ffffff;
}
.gx-mc__panel-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.gx-mc__panel-heading {
  min-width: 0;
}
.gx-mc__panel-heading .gx-mc__section-title {
  margin-bottom: 4px;
}
.gx-mc__panel-heading .gx-mc__section-desc {
  margin-bottom: 0;
}
.gx-mc__panel-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.gx-mc__panel-actions .el-button + .el-button {
  margin-left: 0;
}
.gx-mc__footer-label {
  color: #4a4a6a;
  font-size: 12px;
  white-space: nowrap;
}
.gx-mc__footer-template {
  width: 220px;
}
.gx-mc__state-card {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 14px;
  padding: 28px 24px;
  border: 1px solid #e0e0ec;
  border-radius: 10px;
  background: #ffffff;
  color: #8a95a0;
}
.gx-mc__state-card > i {
  color: #d4a946;
  font-size: 30px;
}
.gx-mc__state-card--error > i {
  color: #c8443c;
}
.gx-mc__state-body {
  flex: 1;
  min-width: 220px;
}
.gx-mc__state-title {
  color: #1a1a2e;
  font-size: 15px;
  font-weight: 600;
}
.gx-mc__state-text {
  margin-top: 4px;
  color: #8a95a0;
  font-size: 12px;
  line-height: 20px;
}
.gx-mc__state-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.gx-mc__option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}
.gx-mc__option-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.gx-mc__option-tags {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.gx-mc__option-meta {
  color: #8a95a0;
  font-size: 12px;
}
.gx-mc__option-warn {
  color: #c8443c;
  font-size: 12px;
}
.template-name-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}
.template-name-cell .el-input {
  flex: 1;
}
.default-template-mark {
  flex: 0 0 auto;
  color: #52a65c;
  font-size: 13px;
}
.sync-dialog-tip {
  margin-bottom: 14px;
  color: #8a95a0;
  font-size: 13px;
  line-height: 20px;
}
.sync-template-select {
  width: 100%;
}
@media (max-width: 680px) {
  .gx-mc__panel-actions .el-button {
    flex: 1;
  }
}
.gx-mc__dialog-card {
  padding: 20px;
  border: 1px solid #e0e0ec;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}
.gx-mc__dialog-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.gx-mc__dialog-title {
  margin: 0;
  color: #1a1a2e;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
}
.gx-mc__dialog-stats {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.gx-mc__stat-badge {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 12px;
  background: #f0f7ff;
  color: #2b7de0;
  font-size: 12px;
  line-height: 24px;
  white-space: nowrap;
}
.gx-mc__dialog-card .gx-mc__alert {
  margin: 12px 0;
}
.gx-mc__dialog-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 14px;
}
.gx-mc__dialog-primary,
.gx-mc__dialog-secondary {
  min-width: 112px;
  height: 34px;
  padding: 0 16px;
  font-size: 13px;
}
.gx-mc__dialog-tip {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin: 12px 0 0;
  color: #8a95a0;
  font-size: 12px;
  line-height: 20px;
}
.gx-mc__dialog-tip i {
  margin-top: 2px;
  color: #2b7de0;
}
.gx-mc__dialog-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #eef0f4;
}
.gx-mc__dialog-note {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #606266;
  font-size: 12px;
  line-height: 20px;
}
.gx-mc__dialog-note i {
  color: #2b7de0;
}
.gx-mc__dialog-footer-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.gx-mc__section-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;
  padding: 12px 64px 12px 16px;
  border: 1px solid #e0e0ec;
  border-radius: 8px;
  background: #ffffff;
}
.gx-mc__section-note {
  color: #8a95a0;
  font-size: 12px;
  line-height: 20px;
}
.gx-mc__section-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.gx-mc__section-actions .el-button + .el-button {
  margin-left: 0;
}
@media (max-width: 900px) {
  .gx-mc__dialog-footer,
  .gx-mc__section-footer {
    align-items: flex-start;
  }
  .gx-mc__dialog-footer-actions,
  .gx-mc__section-actions {
    width: 100%;
  }
}
@media (max-width: 680px) {
  .gx-mc__dialog-primary,
  .gx-mc__dialog-secondary {
    flex: 1;
    width: 100%;
  }
  .gx-mc__dialog-footer-actions .gx-mc__footer-template,
  .gx-mc__section-actions .gx-mc__footer-template {
    flex: 1 0 100%;
    width: 100%;
  }
  .gx-mc__dialog-footer-actions .el-button,
  .gx-mc__section-actions .el-button {
    flex: 1;
  }
}
.gx-mc__template-footer {
  margin-top: 16px;
  padding-top: 14px;
  border: 0;
  border-top: 1px solid #eef0f4;
  border-radius: 0;
}
.gx-mc__install-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 24px;
  padding-top: 14px;
  border-top: 1px solid #eef0f4;
}
.gx-mc__install-footer-note {
  color: #8a95a0;
  font-size: 12px;
  line-height: 20px;
}
.gx-mc__install-footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}
.gx-mc__install-footer-actions .el-button + .el-button {
  margin-left: 0;
}
.gx-mc__install-card {
  padding: 20px;
  border: 1px solid #e0e0ec;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}
.gx-mc__install-body {
  padding-top: 18px;
}
.gx-mc__install-tip {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin: 14px 0 0;
  color: #8a95a0;
  font-size: 12px;
  line-height: 20px;
}
.gx-mc__install-tip i {
  margin-top: 2px;
  color: #c8443c;
}
@media (max-width: 680px) {
  .gx-mc__install-footer-actions .gx-mc__footer-template {
    flex: 1 0 100%;
    width: 100%;
  }
  .gx-mc__install-footer-actions .el-button {
    flex: 1;
  }
}
.gx-mc__attribute-tab {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-top: 14px;
}
.gx-mc__inner-nav {
  position: sticky;
  top: 45px;
  flex: 0 0 180px;
  width: 180px;
  align-self: flex-start;
  padding: 10px;
  border: 1px solid #e0e0ec;
  border-radius: 8px;
  background: #ffffff;
}
.gx-mc__inner-nav-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: #4a4a6a;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  text-align: left;
  transition: color 0.2s, background 0.2s;
}
.gx-mc__inner-nav-item:hover {
  color: #c8443c;
  background: #fff8f8;
}
.gx-mc__inner-nav-item--on {
  background: #fff5f5;
  color: #c8443c;
  font-weight: 600;
  box-shadow: inset 3px 0 0 #c8443c;
}
.gx-mc__inner-content {
  flex: 1;
  min-width: 0;
}
.gx-mc__inner-panel {
  min-width: 0;
}
.gx-mc__attribute-tab .gx-mc__attribute-panel {
  margin-top: 0;
}
.gx-mc__attribute-tab .gx-mc__template-panel {
  margin-top: 0;
}
.gx-setting__row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  color: #303133;
  font-size: 14px;
}

.gx-setting__label {
  white-space: nowrap;
}

.gx-setting__hint {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #606266;
  font-size: 13px;
}

.gx-rule {
  margin-bottom: 8px;
  padding: 8px 12px 0;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background: #ffffff;
}

.gx-rule__line {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 6px;
  color: #303133;
  font-size: 12px;
  line-height: 28px;
}

.gx-rule .el-input__inner {
  height: 28px;
  padding: 0 8px;
  font-size: 12px;
  line-height: 28px;
}

.gx-rule .el-input__icon {
  line-height: 28px;
}

.gx-rule__label {
  font-size: 12px;
}

.gx-rule__price {
  width: 104px;
}

.gx-rule__range {
  white-space: nowrap;
}

.gx-rule__label {
  white-space: nowrap;
}

.gx-rule__label em {
  color: #f56c6c;
  font-style: normal;
}

.gx-rule__sub-label {
  white-space: nowrap;
}

.gx-rule__rate {
  width: 92px;
}

.gx-rule__amount {
  width: 104px;
}

.gx-rule__tail {
  width: 104px;
}

.gx-rule__decimal {
  width: 80px;
}

.gx-rule__mode {
  width: 120px;
}

.gx-rule__unit {
  white-space: nowrap;
}

.gx-rule__tip {
  color: #909399;
  cursor: help;
}

.gx-rule__ops {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.gx-rule__add {
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid #409eff;
  border-radius: 50%;
  background: #ffffff;
  color: #409eff;
  cursor: pointer;
  font-size: 15px;
  line-height: 20px;
}

.gx-rule__add:hover {
  background: #ecf5ff;
}

.gx-rule__delete {
  padding: 0;
  border: 0;
  background: transparent;
  color: #f56c6c;
  cursor: pointer;
  font-size: 16px;
}

.gx-rule__empty-add {
  margin-bottom: 12px;
}
.gx-switch {
  position: relative;
  height: 20px;
}

.gx-switch .el-switch__label--right {
  position: absolute;
  top: 0;
  left: 9px;
  height: 20px;
  margin: 0;
  color: #ffffff;
  font-size: 12px;
  line-height: 20px;
}

.gx-switch:not(.is-checked) .el-switch__label--right {
  display: none;
}
</style>
